import { useCallback, useEffect, useState } from 'react';
import type { IdentityConfig, RelationListItem, TrackRole } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { hub } from '@/lib/hub-client';
import { InviteUserDialog, inviteEnabled } from '../../users/InviteUserDialog';
import { deriveTrackAccess, isOpenToAll, NO_ACCESS, setTrackOpenToAll } from '../track-access';
import { AddUserDialog, type AccessUser } from './AddUserDialog';
import { LevelCell, RoleChips } from './grant-fields';
import { accessErrorMessage, removeUserFromTrack } from './remove-user';

export const LAST_ADMIN_TITLE = 'Last admin';

/** True when removing this user would leave the track with no admin. */
export function isLastAdmin(items: readonly AccessUser[], user: AccessUser): boolean {
  return user.level === 'admin' && items.filter((i) => i.level === 'admin').length === 1;
}

export interface AccessTableProps {
  items: AccessUser[];
  busy?: boolean;
  onEdit: (user: AccessUser) => void;
  onRemove: (user: AccessUser) => void;
}

/** The users table — pure, exported for tests. Remove fails closed on the last admin. */
export function AccessTable({ items, busy, onEdit, onRemove }: AccessTableProps) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No one has been given access yet.</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Level</TableHead>
          <TableHead>Roles</TableHead>
          <TableHead className="w-[1%]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((user) => {
          const lastAdmin = isLastAdmin(items, user);
          return (
            <TableRow key={user.userId}>
              <TableCell>
                <div>{user.name ?? user.email}</div>
                {user.name && <div className="text-xs text-muted-foreground">{user.email}</div>}
              </TableCell>
              <TableCell>
                <LevelCell level={user.level} />
              </TableCell>
              <TableCell>
                <RoleChips roles={user.roles} />
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => onEdit(user)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy || lastAdmin}
                    title={lastAdmin ? LAST_ADMIN_TITLE : undefined}
                    onClick={() => onRemove(user)}
                  >
                    Remove
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/**
 * Access tab of the track page (HUB-109 D7): the one place to add, invite,
 * re-role or remove a user on a track, plus the open-to-all switch shared
 * with the tracks list through `setTrackOpenToAll`.
 */
export function AccessTab({ trackId, trackName }: { trackId: string; trackName: string }) {
  const [items, setItems] = useState<AccessUser[]>([]);
  const [roles, setRoles] = useState<TrackRole[]>([]);
  const [openToAll, setOpenToAll] = useState(false);
  const [identityConfig, setIdentityConfig] = useState<IdentityConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ kind: 'add' | 'edit' | 'invite'; user?: AccessUser } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<AccessUser | null>(null);

  const load = useCallback(async () => {
    try {
      const params = { objectType: 'track', objectId: trackId };
      const [users, roleList, relations] = await Promise.all([
        hub.objectsUsersList({ params }),
        hub.tracksRolesList({ params: { id: trackId } }),
        hub.relationsList({ query: { objectType: 'track', objectId: trackId, includeStructural: false, limit: 500 } }),
      ]);
      setItems(users.items);
      setRoles(roleList.roles);
      const state = deriveTrackAccess(relations.relations as RelationListItem[]).get(trackId) ?? NO_ACCESS;
      setOpenToAll(isOpenToAll(state));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [trackId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    hub.identityConfigGet()
      .then((cfg) => { if (!cancelled) setIdentityConfig(cfg); })
      .catch(() => { /* invite stays disabled */ });
    return () => { cancelled = true; };
  }, []);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (err) {
      setError(accessErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleOpenToAll = (open: boolean) => run(() => setTrackOpenToAll(hub, trackId, open));

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    const user = pendingRemove;
    await run(() => removeUserFromTrack(trackId, user));
    setPendingRemove(null);
  };

  const roleRefs = roles.map((r) => ({ id: r.id, name: r.name }));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">Who can use this track</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy || loading} onClick={() => setDialog({ kind: 'add' })}>
              Add user
            </Button>
            <Button
              disabled={busy || loading || !inviteEnabled(identityConfig)}
              title={inviteEnabled(identityConfig) ? undefined : 'Email sending is off. Configure it under Settings → email.'}
              onClick={() => setDialog({ kind: 'invite' })}
            >
              Invite by email
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <Switch checked={openToAll} disabled={busy || loading} onCheckedChange={(open) => void toggleOpenToAll(open)} aria-label="Open to all hub users" />
            <p className="text-sm">
              <b>Open to all hub users</b>{' '}
              <span className="text-muted-foreground">
                — every signed-in user gets the <b>viewer</b> level (all read tools). This outranks a narrow role.
              </span>
            </p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <AccessTable
              items={items}
              busy={busy}
              onEdit={(user) => setDialog({ kind: 'edit', user })}
              onRemove={setPendingRemove}
            />
          )}
          <p className="text-xs text-muted-foreground">
            <b>Level</b> is the old ladder (admin ⊃ member ⊃ viewer) and is unchanged. <b>Roles</b> are new and add
            up on top. A user with a role and no level can open the track and the copilot, and gets only what the
            role&apos;s ticks give. Remove takes away every level and every role on this track; the last admin cannot
            be removed.
          </p>
        </CardContent>
      </Card>

      <AddUserDialog
        open={dialog?.kind === 'add' || dialog?.kind === 'edit'}
        trackId={trackId}
        roles={roleRefs}
        existing={dialog?.kind === 'edit' ? dialog.user : null}
        onClose={() => setDialog(null)}
        onSaved={() => void load()}
      />

      <InviteUserDialog
        open={dialog?.kind === 'invite'}
        trackId={trackId}
        trackName={trackName}
        trackRoles={roleRefs}
        onClose={() => setDialog(null)}
        onInvited={(email) => {
          setNotice(`Invite sent to ${email}.`);
          void load();
        }}
      />

      <ConfirmDialog
        open={pendingRemove !== null}
        title="Remove from track?"
        description={`${pendingRemove?.email ?? ''} loses every level and every role on ${trackName}.`}
        confirmLabel="Remove"
        destructive
        busy={busy}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setPendingRemove(null)}
      />
    </div>
  );
}
